import styled from "styled-components";
import { styled as MuiStyled } from "@mui/material/styles";
import { colorTokens, ctaAccentColor, textTokens } from "./globalStyles";

// ---------------------------------------------------------------------------
// Icons used by createDynamicIcon (need local binding)
// ---------------------------------------------------------------------------

import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import VerifiedIcon from "@mui/icons-material/Verified";
import DoneIcon from "@mui/icons-material/Done";
import QuizIcon from "@mui/icons-material/Quiz";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DownloadIcon from "@mui/icons-material/Download";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import SchoolIcon from "@mui/icons-material/School";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LaunchIcon from "@mui/icons-material/Launch";
import NotesIcon from "@mui/icons-material/Notes";
import InfoIcon from "@mui/icons-material/Info";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DvrIcon from "@mui/icons-material/Dvr";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import AddBoxIcon from "@mui/icons-material/AddBox";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import HomeRoundedIcon from "@mui/icons-material/Home";
import LogoutRoundedIcon from "@mui/icons-material/Logout";

// Re-export icons that have a local binding (consumed by both S* and raw usage).
export { AnalyticsIcon, InfoIcon, VisibilityIcon };

// ---------------------------------------------------------------------------
// Raw icon re-exports (no styled wrapper, deep imports only)
// ---------------------------------------------------------------------------

export { default as CancelIcon } from "@mui/icons-material/Cancel";
export { default as CheckCircleIcon } from "@mui/icons-material/CheckCircle";
export { default as CloseRoundedIcon } from "@mui/icons-material/CloseRounded";
export { default as DescriptionIcon } from "@mui/icons-material/Description";
export { default as ExitToAppIcon } from "@mui/icons-material/ExitToApp";
export { default as ExpandLessIcon } from "@mui/icons-material/ExpandLess";
export { default as ExpandMoreIcon } from "@mui/icons-material/ExpandMore";
export { default as HistoryIcon } from "@mui/icons-material/History";
export { default as OpenInNewIcon } from "@mui/icons-material/OpenInNew";
export { default as PersonOffIcon } from "@mui/icons-material/PersonOff";
export { default as PhotoCameraIcon } from "@mui/icons-material/PhotoCamera";
export { default as PlayArrowIcon } from "@mui/icons-material/PlayArrow";
export { default as ReportIcon } from "@mui/icons-material/Report";
export { default as UploadFileIcon } from "@mui/icons-material/UploadFile";
export { default as VisibilityOffIcon } from "@mui/icons-material/VisibilityOff";
export { default as WarningIcon } from "@mui/icons-material/Warning";

// ---------------------------------------------------------------------------
// Styled icons (S-prefix, with accent color + hover)
// ---------------------------------------------------------------------------

const createDynamicIcon = (Icon: React.ComponentType, defaultColor: string = ctaAccentColor) =>
  MuiStyled(Icon, {
    shouldForwardProp: (prop) => prop !== "iconColor",
  })<{ iconColor?: string }>(({ theme, iconColor }) => ({
    color: iconColor || defaultColor,
    fontFamily: textTokens.fontFamily,
    cursor: "pointer",
    "&:hover": {
      color: iconColor ? textTokens.color.secondary : "inherit",
    },
    [theme.breakpoints.down("sm")]: {
      transform: "scale(0.8, 0.8)",
    },
  }));

export const SClose = createDynamicIcon(CloseIcon);
export const SEdit = createDynamicIcon(EditIcon);
export const SCreate = createDynamicIcon(AddIcon);
export const SValidate = createDynamicIcon(VerifiedIcon);
export const SDone = createDynamicIcon(DoneIcon);
export const SResultOK = createDynamicIcon(QuizIcon);
export const SResultKO = createDynamicIcon(QuizIcon);
export const SQuizz = createDynamicIcon(AssignmentLateIcon);
export const SRefresh = createDynamicIcon(RefreshIcon);
export const SAddPhoto = createDynamicIcon(AddAPhotoIcon);
export const SNoPhoto = createDynamicIcon(NoPhotographyIcon);
export const SDownloadPhoto = createDynamicIcon(DownloadIcon);
export const SAddDocument = createDynamicIcon(PlaylistAddCircleIcon);
export const SAddCriteria = createDynamicIcon(AddToPhotosIcon);
export const SAddModule = createDynamicIcon(AddBoxIcon);
export const SDownload = createDynamicIcon(DownloadIcon);
export const SView = createDynamicIcon(VisibilityIcon);
export const SCreateFolder = createDynamicIcon(CreateNewFolderIcon);
export const SAnalytics = createDynamicIcon(AnalyticsIcon);
export const SMissing = createDynamicIcon(CloseIcon);
export const SMissingFile = createDynamicIcon(QuestionMarkIcon);
export const SReset = createDynamicIcon(CloseIcon);
export const SSchool = createDynamicIcon(SchoolIcon);
export const SArrow = createDynamicIcon(SubdirectoryArrowRightIcon);
export const SRocket = createDynamicIcon(RocketLaunchIcon);
export const SProduct = createDynamicIcon(ShoppingCartIcon);
export const SInvoice = createDynamicIcon(AttachMoneyIcon);
export const SSettings = createDynamicIcon(DisplaySettingsIcon);
export const STemplates = createDynamicIcon(DvrIcon);
export const SCreditCard = createDynamicIcon(CreditCardIcon);
export const SSendMail = createDynamicIcon(ForwardToInboxIcon);
export const SStats = createDynamicIcon(QueryStatsIcon);
export const SUsers = createDynamicIcon(GroupIcon);
export const SGroups = createDynamicIcon(GroupsIcon);
export const SAddOption = createDynamicIcon(AddIcon);
export const SDelete = createDynamicIcon(DeleteForeverIcon, colorTokens.status.danger);
export const SSave = createDynamicIcon(SaveIcon);
export const SFile = createDynamicIcon(FilePresentIcon);
export const SDate = createDynamicIcon(AccessTimeIcon);
export const SInfo = createDynamicIcon(InfoIcon);
export const SUserAdd = createDynamicIcon(PersonAddIcon);
export const SRedirect = createDynamicIcon(LaunchIcon);
export const SNotes = createDynamicIcon(NotesIcon);
export const SGenerate = createDynamicIcon(FileOpenIcon);
export const SOpenProfile = createDynamicIcon(AccountCircleIcon);
export const SHome = createDynamicIcon(HomeRoundedIcon);
export const SLogout = createDynamicIcon(LogoutRoundedIcon);

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

export const SBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: "calc(100vh - 64px)";
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.5);
`;

export const SPhoto = styled.img`
  width: 100%;
  border-radius: 50%;
`;
